import React, { useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { FaTimes } from "react-icons/fa";

const sizeMap = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  full: "max-w-[90vw]",
};

function ModalHeader({ title, subtitle, onClose, children }) {
  if (children) {
    return (
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
        {children}
        {onClose && (
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 shrink-0 ml-3">
            <FaTimes className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
      <div>
        {title && <h3 className="text-lg font-semibold text-gray-800">{title}</h3>}
        {subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}
      </div>
      {onClose && (
        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 shrink-0 ml-3">
          <FaTimes className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

function ModalBody({ children, className = "" }) {
  return <div className={`px-5 py-4 ${className}`}>{children}</div>;
}

function ModalFooter({ children, className = "" }) {
  return (
    <div className={`px-5 py-4 border-t border-gray-200 ${className}`}>
      {children}
    </div>
  );
}

function Modal({
  open,
  onClose,
  maxWidth = "lg",
  children,
  className = "",
  zIndex = 50,
}) {
  const handleEsc = useCallback(
    (e) => {
      if (e.key === "Escape" && onClose) onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (!open) return;
    document.addEventListener("keydown", handleEsc);
    document.body.classList.add("overflow-hidden");
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.classList.remove("overflow-hidden");
    };
  }, [open, handleEsc]);

  if (!open) return null;

  const widthClass = sizeMap[maxWidth] || sizeMap.lg;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ zIndex }}
    >
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div
        className={`relative bg-white rounded-2xl shadow-2xl w-full ${widthClass} mx-4 border border-gray-200 ${className}`}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

Modal.Header = ModalHeader;
Modal.Body = ModalBody;
Modal.Footer = ModalFooter;

export default Modal;
